/**
 * Page geometry, type scale, palette and drawing primitives for the AI Analysis report PDF.
 *
 * Conventions follow the Verification report builder (src/features/Verification/utils/reportLayout.js):
 * millimetres for geometry, `[r, g, b]` triples for colour (jsPDF and CSS disagree about which colour STRINGS
 * they accept; a triple is unambiguous), and Helvetica because it is the font jsPDF measures with.
 */

/**
 * A3 landscape (420 x 297 mm) - the size of the PM's reference PDF, verified from its MediaBox. Twice A4's
 * area, which is what lets the summary, the findings table and the annotated scan share one sheet the way
 * the reference does. Khalid, 2026-09-07: "apply the reference as the pdf export".
 */
export const PAGE = Object.freeze({
  width: 420,
  height: 297,
  margin: 12,
  /** Reserved at the bottom of every page for the footnote and "Page N of M". */
  footerGutter: 11,
});

export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;
export const CONTENT_BOTTOM = PAGE.height - PAGE.footerGutter;

/** Point sizes. The body sizes are what the column geometry below was tuned against; do not scale them blindly. */
export const TYPE = Object.freeze({
  wordmark: 22,
  title: 15,
  subtitle: 8.5,
  sectionHead: 9,
  metaLabel: 6.5,
  metaValue: 9,
  kpiLabel: 6,
  kpiValue: 15,
  tableHead: 6.5,
  tableBody: 6.5,
  body: 7.5,
  cardTitle: 8,
  caption: 6.5,
  chrome: 7,
});

/** Tracks the feature's SCSS tokens in _aiAnalysisTokens.scss and the report showcase. */
export const COLOR = Object.freeze({
  ink: Object.freeze([2, 26, 26]),
  body: Object.freeze([51, 65, 85]),
  soft: Object.freeze([94, 110, 133]),
  rule: Object.freeze([214, 219, 223]),
  brand: Object.freeze([3, 98, 76]),
  alert: Object.freeze([198, 40, 40]),
  pending: Object.freeze([217, 119, 6]),
  white: Object.freeze([255, 255, 255]),
  surface: Object.freeze([248, 250, 252]),
  badge: Object.freeze([198, 40, 40]),
  contourHigh: Object.freeze([211, 47, 47]),
  contourModerate: Object.freeze([239, 138, 23]),
  contourLow: Object.freeze([230, 180, 0]),
  scabYes: Object.freeze([183, 28, 28]),
  scabPossible: Object.freeze([180, 83, 9]),
  scabNo: Object.freeze([3, 98, 76]),
});

/** Points to millimetres, for turning a type size into a line height. */
const PT_TO_MM = 0.3528;
const LINE_FACTOR = 1.35;

export function lineHeight(size) {
  return size * PT_TO_MM * LINE_FACTOR;
}

/** '#ef8a17' -> [239, 138, 23]. Used where a colour arrives from the shared token helpers as hex. */
export function hexToRgb(hex) {
  const value = String(hex).replace('#', '');
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
}

/* ------------------------------------------------------------------------------------------------------
   Primitives. Each one leaves the document's font and colour state set to whatever it used, so callers set
   what they need rather than assuming what the last call left behind.
   ------------------------------------------------------------------------------------------------------ */

export function setText(doc, { size = TYPE.body, style = 'normal', color = COLOR.ink } = {}) {
  doc.setFontSize(size).setFont('helvetica', style);
  doc.setTextColor(...color);
}

/** Draws a single line and returns the y a following line would start on. */
export function text(doc, value, x, y, options = {}) {
  setText(doc, options);
  doc.text(String(value == null ? '' : value), x, y, { align: options.align || 'left', baseline: 'top' });
  return y + lineHeight(options.size || TYPE.body);
}

/** Wraps into `width` mm and returns the y after the last line. Pass `maxLines` to clamp with an ellipsis. */
export function paragraph(doc, value, x, y, width, options = {}) {
  setText(doc, options);
  let lines = doc.splitTextToSize(String(value == null ? '' : value), width);
  if (options.maxLines && lines.length > options.maxLines) {
    lines = lines.slice(0, options.maxLines);
    lines[lines.length - 1] = `${String(lines[lines.length - 1]).replace(/\s+\S*$/, '')}...`;
  }
  const step = lineHeight(options.size || TYPE.body);
  lines.forEach((line, i) => doc.text(line, x, y + i * step, { align: options.align || 'left', baseline: 'top' }));
  return y + lines.length * step;
}

/** Height `paragraph` would occupy, so a caller can decide about a page break before drawing anything. */
export function paragraphHeight(doc, value, width, options = {}) {
  setText(doc, options);
  const lines = doc.splitTextToSize(String(value == null ? '' : value), width);
  const count = options.maxLines ? Math.min(lines.length, options.maxLines) : lines.length;
  return count * lineHeight(options.size || TYPE.body);
}

export function rule(doc, x, y, width, color = COLOR.rule) {
  doc.setDrawColor(...color).setLineWidth(0.2);
  doc.line(x, y, x + width, y);
}

export function box(doc, x, y, width, height, { fill, stroke = COLOR.rule, radius = 1 } = {}) {
  if (fill) doc.setFillColor(...fill);
  doc.setDrawColor(...stroke).setLineWidth(0.2);
  const style = fill ? 'FD' : 'D';
  doc.roundedRect(x, y, width, height, radius, radius, style);
}

/** A filled pill with centred label - the scab and PROPOSED chips. Returns its width. */
export function chip(doc, label, x, y, { fill, color = COLOR.white, size = TYPE.caption, padding = 1.6 } = {}) {
  setText(doc, { size, style: 'bold', color });
  const width = doc.getTextWidth(String(label)) + padding * 2;
  const height = lineHeight(size) + 1;
  doc.setFillColor(...fill);
  doc.roundedRect(x, y, width, height, height / 2, height / 2, 'F');
  doc.setTextColor(...color);
  doc.text(String(label), x + width / 2, y + height / 2, { align: 'center', baseline: 'middle' });
  return width;
}

export default { PAGE, TYPE, COLOR, CONTENT_WIDTH, CONTENT_BOTTOM };
